// 7 Vorlagen-Themes
export const PRESET_THEMES = [
  {
    id: 'sap_fiori_blue',
    name: 'SAP Fiori Horizon',
    description: 'Enterprise Business Light Blue',
    descKey: 'theme.descFiori',
    primary: '#0070F2',
    accent: '#0070F2',
    sidebar: '#1B2A4A',
    bg: '#F7F7F7',
    preview: ['#0070F2', '#60A5FA', '#1B2A4A'],
  },
  {
    id: 'qm8d_classic',
    name: 'QM8D Classic',
    description: 'Klassisches Unternehmensdesign',
    descKey: 'theme.descClassic',
    primary: '#00539f',
    accent: '#0070F2',
    sidebar: '#1a1d23',
    bg: '#f8fafc',
    preview: ['#00539f', '#0070F2', '#1a1d23'],
  },
  {
    id: 'ocean_blue',
    name: 'Ocean Blue',
    description: 'Klar und professionell',
    descKey: 'theme.descOcean',
    primary: '#0369a1',
    accent: '#38bdf8',
    sidebar: '#0c1929',
    bg: '#f0f9ff',
    preview: ['#0369a1', '#38bdf8', '#0c1929'],
  },
  {
    id: 'forest_green',
    name: 'Forest Green',
    description: 'Natürlich und ruhig',
    descKey: 'theme.descForest',
    primary: '#15803d',
    accent: '#4ade80',
    sidebar: '#0a1f12',
    bg: '#f0fdf4',
    preview: ['#15803d', '#4ade80', '#0a1f12'],
  },
  {
    id: 'sunset_orange',
    name: 'Sunset Orange',
    description: 'Warm und energetisch',
    descKey: 'theme.descSunset',
    primary: '#c2410c',
    accent: '#fb923c',
    sidebar: '#1c1210',
    bg: '#fff7ed',
    preview: ['#c2410c', '#fb923c', '#1c1210'],
  },
  {
    id: 'royal_purple',
    name: 'Royal Purple',
    description: 'Elegant und modern',
    descKey: 'theme.descRoyal',
    primary: '#7c3aed',
    accent: '#a78bfa',
    sidebar: '#1a0f2e',
    bg: '#faf5ff',
    preview: ['#7c3aed', '#a78bfa', '#1a0f2e'],
  },
  {
    id: 'slate_modern',
    name: 'Slate Modern',
    description: 'Minimalistisch und neutral',
    descKey: 'theme.descSlate',
    primary: '#475569',
    accent: '#94a3b8',
    sidebar: '#0f172a',
    bg: '#f8fafc',
    preview: ['#475569', '#94a3b8', '#0f172a'],
  },
];

// Helper: Hex -> RGBA
export function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Helper: Helligkeit anpassen
export function adjustBrightness(hex, percent) {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  if (percent > 0) {
    r = Math.min(255, r + Math.round((255 - r) * percent / 100));
    g = Math.min(255, g + Math.round((255 - g) * percent / 100));
    b = Math.min(255, b + Math.round((255 - b) * percent / 100));
  } else {
    const factor = (100 + percent) / 100;
    r = Math.round(r * factor);
    g = Math.round(g * factor);
    b = Math.round(b * factor);
  }
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Helper: Kontrasttext fuer Hintergrund
export function getContrastText(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1d23' : '#ffffff';
}

// CSS-Variablen live anwenden
// Bug #3: Im Dark-Mode werden User-Farben automatisch aufgehellt fuer Lesbarkeit
export const applyThemeVars = (prefs) => {
  const root = document.documentElement;
  const darkActive = prefs.dark_mode === true
    || (prefs.dark_mode === undefined && root.classList.contains('dark'));

  // Bei Dark-Mode: Primary/Accent aufhellen, Sidebar abdunkeln (besserer Kontrast)
  const darkBoost = (hex, positivePct) => darkActive ? adjustBrightness(hex, positivePct) : hex;

  if (prefs.primary_color) {
    const primary = darkBoost(prefs.primary_color, 25);
    root.style.setProperty('--g-primary', primary);
    root.style.setProperty('--g-primary-hover', adjustBrightness(primary, darkActive ? 10 : -15));
    root.style.setProperty('--g-primary-light', adjustBrightness(prefs.primary_color, darkActive ? 40 : 85));
  }
  if (prefs.accent_color) {
    const accent = darkBoost(prefs.accent_color, 15);
    root.style.setProperty('--g-accent', accent);
    root.style.setProperty('--g-accent-hover', adjustBrightness(accent, darkActive ? 10 : -15));
    root.style.setProperty('--sidebar-accent', accent);
    root.style.setProperty('--sidebar-accent-bg', hexToRgba(accent, 0.12));
    root.style.setProperty('--sidebar-accent-bg-strong', hexToRgba(accent, 0.18));
    root.style.setProperty('--sidebar-brand-bg', accent);
  }
  if (prefs.sidebar_color) {
    // Sidebar im Dark-Mode zusaetzlich abdunkeln fuer konsistentes Gesamtbild
    const sidebar = darkActive ? adjustBrightness(prefs.sidebar_color, -25) : prefs.sidebar_color;
    root.style.setProperty('--sidebar-bg', sidebar);
    root.style.setProperty('--sidebar-brand-text', getContrastText(sidebar));
  }
  if (prefs.bg_color) {
    // Hintergrund: Im Dark-Mode nicht anwenden (dark CSS uebernimmt)
    if (!darkActive) {
      root.style.setProperty('--g-bg', prefs.bg_color);
    }
  }
  if (prefs.dark_mode !== undefined) {
    if (prefs.dark_mode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('qm8d_theme', prefs.dark_mode ? 'dark' : 'light');
    // Bei Dark-Mode-Wechsel: Farben re-appliziere, da darkActive sich geaendert hat
    if (prefs.primary_color || prefs.accent_color || prefs.sidebar_color) {
      // Rekursion vermeiden: nur wenn keine Farbe dabei war
    } else {
      const cached = readCachedThemePrefs();
      if (cached) {
        applyThemeVars({
          primary_color: cached.primary_color,
          accent_color: cached.accent_color,
          sidebar_color: cached.sidebar_color,
          bg_color: cached.bg_color,
        });
      }
    }
  }
};

// Bug #2: LocalStorage-Cache fuer komplettes Theme (verhindert FOUC beim Reload)
const THEME_CACHE_KEY = 'qm8d_theme_prefs';

export const cacheThemePrefs = (prefs) => {
  try {
    const cached = {
      theme_id: prefs.theme_id,
      primary_color: prefs.primary_color,
      accent_color: prefs.accent_color,
      sidebar_color: prefs.sidebar_color,
      bg_color: prefs.bg_color,
      dark_mode: prefs.dark_mode,
    };
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // LocalStorage nicht verfuegbar (private mode) — ignorieren
  }
};

export const readCachedThemePrefs = () => {
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Bootstrap-Aufruf vor React-Mount (verhindert Flash of Unstyled Content)
export const bootstrapThemeFromCache = () => {
  const cached = readCachedThemePrefs();
  if (cached) {
    applyThemeVars(cached);
  }
};
